import React, { memo } from 'react';
import { Card } from './ui/card';

type SkeletonProps = {
  showHeader?: boolean;
  showProsCons?: boolean;
  showRecommendation?: boolean;
  showImprovement?: boolean;
  showQuestions?: boolean;
  showStats?: boolean;
  centerContent?: React.ReactNode;
  theme?: 'dark' | 'light';
};

// Быстрая анимация блеска через градиент
const shimmer = 'animate-shimmer rounded';

const AnalysisResultSkeleton: React.FC<SkeletonProps> = ({
  showHeader = true,
  showProsCons = true,
  showRecommendation = true,
  showImprovement = true,
  showQuestions = false,
  showStats = false,
  centerContent,
  theme = 'light',
}) => {
  const isDark = theme === 'dark';
  return (
    <div className={`min-h-screen w-full ${
      isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-white'
    }`}>
      <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 py-6 sm:py-8 relative z-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="min-h-screen py-12 space-y-16 pb-24 lg:pb-12">
            
            {/* AnalysisHeaderV3 Skeleton */}
            {showHeader && (
              <div className="space-y-6">
                {/* Header Card */}
                <Card className={`relative border-0 shadow-xl p-8 space-y-6 ${
                  isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white'
                }`}>
                  {/* Header info */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`h-4 w-4 rounded ${shimmer}`} />
                      <div className={`h-4 w-32 rounded ${shimmer}`} />
                    </div>
                    <div className={`h-4 w-[52px] rounded-full ${shimmer}`} />
                  </div>
                  
                  {/* Title - Основной текст запроса (H1) */}
                  <div className="flex flex-col items-center space-y-4 max-w-4xl mx-auto w-full">
                    {/* Имитация крупного текста - 1 строка */}
                    <div className={`h-8 sm:h-10 rounded-md ${shimmer}`} style={{ width: '75%' }} />
                  </div>
                  
                  {/* Spacer (Empty space where Result Card was) или кастомный контент */}
                  {centerContent ? (
                    <div className="flex items-center justify-center !-mb-4">
                      {centerContent}
                    </div>
                  ) : (
                    <div className="py-8 h-48" />
                  )}
                  
                  {/* Stats Grid - 3 блока (Плюсы, Минусы, Уверенность) */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={`stat-${idx}`} className={`flex flex-col justify-between p-6 rounded-xl h-36 ${
                        isDark ? 'bg-slate-700/30 border border-slate-600' : 'bg-gray-50/50'
                      }`}>
                        {/* Label & Value */}
                        <div className="space-y-3 w-full">
                          <div className={`h-5 w-24 rounded ${shimmer}`} />
                          <div className={`h-8 w-16 rounded ${shimmer}`} />
                        </div>
                        
                        {/* Progress Bar Skeleton */}
                        <div className="w-full pt-2">
                          <div className={`h-2.5 w-full rounded-full ${shimmer}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

          {/* ProsConsV3 Skeleton */}
          {showProsCons && (
            <div className="space-y-8">
              {/* Section Header */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="text-center lg:text-left space-y-4">
                  <div className={`h-8 w-64 rounded-lg ${shimmer}`} />
                  <div className={`h-6 w-96 rounded-lg ${shimmer}`} />
                </div>
                <div className={`h-10 w-24 rounded-lg ${shimmer}`} />
              </div>
              
              {/* Pros and Cons Grid */}
              <div className="grid gap-8 lg:grid-cols-2">
                {/* Pros Column */}
                <div className="space-y-6">
                  {/* Column Title */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-br from-green-500/5 to-emerald-600/5 border border-green-500/10">
                    <div className={`h-6 w-32 rounded ${shimmer}`} />
                    <div className={`h-6 w-8 rounded-full ${shimmer}`} />
                  </div>
                  
                  {/* Argument Cards */}
                  <div className="space-y-4">
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <Card key={`pro-${idx}`} className={`p-4 ${
                        isDark ? 'bg-slate-800/50 border-slate-700' : ''
                      }`}>
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div className={`h-5 w-3/4 rounded ${shimmer}`} />
                            <div className={`h-5 w-8 rounded ${shimmer}`} />
                          </div>
                          <div className={`h-4 w-full rounded ${shimmer}`} />
                          <div className={`h-4 w-5/6 rounded ${shimmer}`} />
                          <div className="flex items-center gap-1 pt-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div key={`star-${i}`} className={`h-4 w-4 rounded-full ${shimmer}`} />
                            ))}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
                
                {/* Cons Column */}
                <div className="space-y-6">
                  {/* Column Title */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-br from-red-500/5 to-rose-600/5 border border-red-500/10">
                    <div className={`h-6 w-32 rounded ${shimmer}`} />
                    <div className={`h-6 w-8 rounded-full ${shimmer}`} />
                  </div>
                  
                  {/* Argument Cards */}
                  <div className="space-y-4">
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <Card key={`con-${idx}`} className={`p-4 ${
                        isDark ? 'bg-slate-800/50 border-slate-700' : ''
                      }`}>
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div className={`h-5 w-3/4 rounded ${shimmer}`} />
                            <div className={`h-5 w-8 rounded ${shimmer}`} />
                          </div>
                          <div className={`h-4 w-full rounded ${shimmer}`} />
                          <div className={`h-4 w-5/6 rounded ${shimmer}`} />
                          <div className="flex items-center gap-1 pt-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div key={`star-${i}`} className={`h-4 w-4 rounded-full ${shimmer}`} />
                            ))}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RecommendationV3 Skeleton */}
          {showRecommendation && (
            <div className="space-y-8">
              {/* Section Header */}
              <div className="text-center space-y-4">
                <div className={`h-8 w-80 mx-auto rounded-lg ${shimmer}`} />
                <div className={`h-6 w-96 mx-auto rounded-lg ${shimmer}`} />
              </div>
              
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Final Verdict Card */}
                  <Card className={`p-6 border-l-4 border-l-blue-500 ${
                    isDark ? 'bg-slate-800/50 border-slate-700' : ''
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-xl flex-shrink-0 ${shimmer}`} />
                      <div className="space-y-2 flex-1">
                        <div className={`h-6 w-48 rounded ${shimmer}`} />
                        <div className={`h-4 w-full rounded ${shimmer}`} />
                        <div className={`h-4 w-3/4 rounded ${shimmer}`} />
                      </div>
                    </div>
                  </Card>

                  {/* Main Recommendation */}
                  <Card className={`p-8 space-y-4 ${
                    isDark 
                      ? 'bg-gradient-to-br from-blue-900/20 to-indigo-900/20 border-blue-800/30' 
                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
                  }`}>
                    <div className={`h-6 w-64 rounded ${shimmer}`} />
                    <div className="space-y-2">
                      <div className={`h-4 w-full rounded ${shimmer}`} />
                      <div className={`h-4 w-full rounded ${shimmer}`} />
                      <div className={`h-4 w-full rounded ${shimmer}`} />
                      <div className={`h-4 w-5/6 rounded ${shimmer}`} />
                    </div>
                  </Card>

                  {/* Key Factors */}
                  <Card className={`p-6 ${
                    isDark ? 'bg-slate-800/50 border-slate-700' : ''
                  }`}>
                    <div className={`h-6 w-48 mb-4 rounded ${shimmer}`} />
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <div key={`factor-${idx}`} className="flex items-center gap-3">
                          <div className={`h-2 w-2 rounded-full ${shimmer}`} />
                          <div className={`h-4 w-full rounded ${shimmer}`} />
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Action Plan */}
                  <Card className={`p-6 ${
                    isDark ? 'bg-slate-800/50 border-slate-700' : ''
                  }`}>
                    <div className={`h-6 w-40 mb-4 rounded ${shimmer}`} />
                    <div className="space-y-4">
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <div key={`step-${idx}`} className={`p-3 rounded-lg border ${
                          isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-100'
                        }`}>
                          <div className="flex gap-3">
                            <div className={`h-6 w-6 rounded-full flex-shrink-0 ${shimmer}`} />
                            <div className="space-y-2 flex-1">
                              <div className={`h-4 w-full rounded ${shimmer}`} />
                              <div className={`h-3 w-20 rounded ${shimmer}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Share Buttons */}
                  <Card className={`p-6 ${
                    isDark ? 'bg-slate-800/50 border-slate-700' : ''
                  }`}>
                    <div className={`h-6 w-32 mb-4 rounded ${shimmer}`} />
                    <div className="flex flex-col gap-3">
                      <div className={`h-10 w-full rounded-lg ${shimmer}`} />
                      <div className={`h-10 w-full rounded-lg ${shimmer}`} />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          )}

          {/* ImprovementSuggestionsV3 Skeleton */}
          {showImprovement && (
            <div className="space-y-6">
              <div className="text-center">
                <div className={`h-8 w-80 mx-auto rounded-lg mb-2 ${shimmer}`} />
                <div className={`h-5 w-64 mx-auto rounded-lg ${shimmer}`} />
              </div>
              
              <Card className={`p-6 border-dashed border-2 ${
                isDark ? 'border-slate-600 bg-slate-800/30' : 'border-gray-200 bg-gray-50/50'
              }`}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className={`h-4 w-full rounded ${shimmer}`} />
                    <div className={`h-4 w-full rounded ${shimmer}`} />
                    <div className={`h-4 w-3/4 rounded ${shimmer}`} />
                  </div>
                  <div className={`h-10 w-48 rounded-lg ${shimmer}`} />
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
    
    <style>{`
      @keyframes shimmer-bg {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      .animate-shimmer {
        background: ${isDark 
          ? 'linear-gradient(90deg, #475569 0%, #64748b 40%, #64748b 60%, #475569 100%)'
          : 'linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 40%, #f3f4f6 60%, #e5e7eb 100%)'};
        background-size: 200% 100%;
        animation: shimmer-bg 5s infinite linear;
      }
    `}</style>
  </div>
  );
};

export default memo(AnalysisResultSkeleton);
